import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import AvatarImage from '../../room/message-display-area/messages/utils/AvatarImage.jsx';
import withMessage from '../../room/message-display-area/messages/withMessage.jsx';

import { MessageTypes } from '../../room/message-display-area/messages/constants/index.js';
import { composeClassName } from '../../room/message-display-area/messages/utils/common';

let resizeListener = null;

const MIN_WIDTH = 50;
const MIN_HEIGHT = 50;
const SMALL_WIDTH = 240;
const MEDIUM_WIDTH = 300;
const BIG_WIDTH = 400;
const BIG_SCREEN_BORDER = 510;
const SMALL_SCREEN_BORDER = 400;
const GET_CLIENT_WIDTH = () => window.document.documentElement.clientWidth;
const IS_SMALL_SCREEN = () => GET_CLIENT_WIDTH() < SMALL_SCREEN_BORDER;
const IS_MEDIUM_SCREEN = () => GET_CLIENT_WIDTH() > SMALL_SCREEN_BORDER && GET_CLIENT_WIDTH() < BIG_SCREEN_BORDER;
const IS_BIG_SCREEN = () => GET_CLIENT_WIDTH() > BIG_SCREEN_BORDER;

const LinkPreview = ({
  domain, title, description, image, url, type, isFirstInGroup, isLastInGroup, messageViewType, imgParams, nickname,
}) => {
  const imageRef = useRef(null);
  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.style.display = 'none';
    }
    return () => window.removeEventListener('resize', resizeListener);
  }, []);

  const shouldShowPreview = !!(title || description || image);
  if (!shouldShowPreview) return null;

  function calculateAndResizeImage(width, height) {
    const ratio = width / height;
    let newWidth = width;
    let newHeight = height;
    const maxWidth = IS_SMALL_SCREEN() ? SMALL_WIDTH : (IS_BIG_SCREEN() ? BIG_WIDTH : MEDIUM_WIDTH);

    if (width > maxWidth || height > maxWidth) {
      const isWidthBigger = width > height;
      const isEven = width === height;

      newWidth = (isWidthBigger || isEven) ? maxWidth : (height * ratio);
      newHeight = isWidthBigger ? (maxWidth / ratio) : maxWidth;
    }

    imageRef.current.width = newWidth;
    imageRef.current.height = newHeight;
    imageRef.current.style.display = 'block';
  }

  function handleImageLoaded(event) {
    const { width, height } = event.target;
    if (width < MIN_WIDTH || height < MIN_HEIGHT) return;

    calculateAndResizeImage(width, height);

    resizeListener = window.addEventListener('resize', () => {
      const { width: currentImageWidth } = imageRef.current;
      const shouldResizeImage =
        ((currentImageWidth === BIG_WIDTH) && !IS_BIG_SCREEN()) ||
        ((currentImageWidth === SMALL_WIDTH) && !IS_SMALL_SCREEN()) ||
        ((currentImageWidth === MEDIUM_WIDTH) && !IS_MEDIUM_SCREEN());

      if (shouldResizeImage) {
        calculateAndResizeImage(width, height);
      }
    })
  }

  function handleOpenPreview() {
    window.open(url);
  }

  const className = composeClassName(type, isFirstInGroup, isLastInGroup);

  return (
    <div className={`preview ${className}`}>
      {type === MessageTypes.OPERATOR && <AvatarImage {...{ messageViewType, imgParams, nickname }} />}
      <div className="message-content">
        <div className="message-text">
          <div className="link-preview">
            <img
              onClick={handleOpenPreview}
              ref={imageRef}
              onLoad={handleImageLoaded}
              src={image}
              alt=""
              className="link-preview__image"
            />
            <div onClick={handleOpenPreview} className="link-preview__text-content">
              <span className="link-preview__title">{title}</span>
              <span className="link-preview__description">{description}</span>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={url}
              className="link-preview__description"
            >
              {domain}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

LinkPreview.propTypes = {
  domain: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  isFirstInGroup: PropTypes.bool,
  isLastInGroup: PropTypes.bool,
  messageViewType: PropTypes.any,
  imgParams: PropTypes.object,
  nickname: PropTypes.string,
}

export default withMessage(LinkPreview);
